import React, { useState, useEffect } from 'react';
import { useApi, fmtKST } from '../hooks/useApi';

// NEX-1269: 퀀트 후보·기준선 백테스트 지표 (읽기 전용, 모의 전용)
const pct = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(2)}%`);

export default function QuantPanel() {
  const { data, loading, error } = useApi('/api/quant/summary');
  const [periodKey, setPeriodKey] = useState('out_of_sample');
  const [failedAt, setFailedAt] = useState(null);

  // 실패 시각은 렌더링 때마다 새로 만들지 않고 오류가 처음 관측된 시점에 고정한다.
  useEffect(() => { setFailedAt(error ? new Date().toISOString() : null); }, [error]);

  if (error) return <Notice tone="red" text={`퀀트 API 호출 실패: ${error} · 실패 시각 ${fmtKST(failedAt)}`} />;
  if (loading || !data) return <div className="text-gray-500 text-sm">불러오는 중...</div>;

  const period = data.periods?.find((p) => p.key === periodKey) || data.periods?.[0];

  return (
    <div className="space-y-4">
      <AttackSignalToday />

      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 text-xs text-gray-400 space-y-1">
        <div className="text-sm text-gray-200 font-semibold">퀀트 전략 검증 (모의·백테스트 전용, 주문 없음)</div>
        <div>출처: {data.source}</div>
        <div>신호: {data.assumptions?.signal} · 편도 수수료 {pct(data.assumptions?.fee_one_way)} · 슬리피지 {pct(data.assumptions?.slippage_one_way)}</div>
        <div>승률 정의: {data.assumptions?.win_rate}</div>
        <div className="text-yellow-300">레버리지 위험 통제: {data.assumptions?.risk}</div>
        <div className="text-yellow-300">일일 재설정 주의: {data.daily_reset_warning}</div>
        {data.generated_at && <div>원자료 파일 갱신: {fmtKST(data.generated_at)}</div>}
      </div>

      {data.status === 'error' && <Notice tone="red" text={`백테스트 원자료 로드 실패: ${data.error} · 실패 시각 ${fmtKST(data.failed_at)}`} />}
      {data.status === 'empty' && <Notice tone="yellow" text={data.message} />}
      {data.fx?.status !== 'ok' && <Notice tone="yellow" text={data.fx?.message} />}

      {period && (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {data.periods.map((p) => (
              <button key={p.key} onClick={() => setPeriodKey(p.key)}
                className={`px-3 py-1 rounded text-xs ${p.key === period.key ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}>
                {p.label}
              </button>
            ))}
            <span className="text-xs text-gray-500 ml-auto">
              유효구간 {period.data_range ? `${period.data_range.start} ~ ${period.data_range.end} (${period.data_range.rows}일)` : '데이터 없음'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>{['전략', '구분', '누적수익률', 'CAGR', '승률', 'MDD', '샤프', '변동성', '회전율', '거래 수'].map((h) => <th key={h} className="text-left py-1 pr-3">{h}</th>)}</tr>
              </thead>
              <tbody className="text-gray-200">
                {period.strategies.map((s) => (
                  <tr key={s.strategy} className="border-t border-gray-700">
                    <td className="py-1 pr-3">{s.strategy}</td>
                    <td className="pr-3">{s.role}</td>
                    {s.status === 'ok' ? (
                      <>
                        <td className="pr-3">{pct(s.cumulative_return)}</td>
                        <td className="pr-3">{pct(s.cagr)}</td>
                        <td className="pr-3">{pct(s.win_rate)}</td>
                        <td className="pr-3 text-red-400">{pct(s.mdd)}</td>
                        <td className="pr-3">{s.sharpe.toFixed(2)}</td>
                        <td className="pr-3">{pct(s.volatility)}</td>
                        <td className="pr-3">{s.turnover.toFixed(2)}</td>
                        <td className="pr-3">{s.trades}</td>
                      </>
                    ) : (
                      <td colSpan={8} className="text-yellow-400">{s.reason}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

// NEX-1290: TQQQ/SOXL 공격형 슬롯(PORT_ATTACK) 오늘 신호 + 모의 보유 — 백테스트가 아니라
// 실제 매일 스케줄러가 계산·기록한 신호/주문 경로(attack_signal_daily, attack_rebalance_daily).
function AttackSignalToday() {
  const { data, loading, error } = useApi('/api/quant/signals/today');

  if (loading) return <div className="text-gray-500 text-sm">공격형 슬롯 신호 불러오는 중...</div>;
  if (error) return <Notice tone="red" text={`공격형 슬롯 신호 API 실패: ${error}`} />;

  const signals = data?.signals || [];

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      <div className="text-sm text-gray-200 font-semibold mb-2">
        TQQQ·SOXL 공격형 슬롯 — 오늘 신호 / 모의 보유 (PORT_ATTACK)
      </div>
      {signals.length === 0 ? (
        <div className="text-xs text-gray-500">{data?.message || '오늘 신호 없음 — attack_signal_daily 미실행 또는 집계 지연'}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>{['티커', '신호일(T 종가 기준)', '종가', '목표비중', '신호 산출시각', '모의 보유 수량', '모의 보유 평균단가'].map((h) => (
                <th key={h} className="text-left py-1 pr-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="text-gray-200">
              {signals.map((s) => (
                <tr key={s.ticker} className="border-t border-gray-700">
                  <td className="py-1 pr-3">{s.ticker}</td>
                  <td className="pr-3">{s.signal_date}</td>
                  <td className="pr-3">${Number(s.close_price).toFixed(2)}</td>
                  <td className="pr-3">{(s.target_weight * 100).toFixed(2)}%</td>
                  <td className="pr-3">{fmtKST(s.computed_at)}</td>
                  <td className="pr-3">{s.paper_holding?.quantity ?? 0}주</td>
                  <td className="pr-3">
                    {s.paper_holding?.avg_price != null ? `$${Number(s.paper_holding.avg_price).toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Notice({ tone, text }) {
  const cls = tone === 'red' ? 'border-red-500 bg-red-900/20 text-red-300' : 'border-yellow-500 bg-yellow-900/20 text-yellow-300';
  return <div className={`rounded-lg p-3 border text-xs ${cls}`}>{text}</div>;
}
